import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

type ToastKind = "success" | "error" | "info";
interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastApi {
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const STYLES: Record<ToastKind, { color: string; Icon: typeof Info }> = {
  success: { color: "#2DD4BF", Icon: CheckCircle2 },
  error: { color: "#EF4444", Icon: AlertCircle },
  info: { color: "#38BDF8", Icon: Info },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string) => {
      const id = ++seq.current;
      setToasts((t) => [...t, { id, kind, message }]);
      setTimeout(() => dismiss(id), 3800);
    },
    [dismiss]
  );

  // `push` is stable (useCallback over a stable `dismiss`), so this api object
  // built once stays valid for the provider's lifetime.
  const api = useRef<ToastApi>({
    success: (m) => push("success", m),
    error: (m) => push("error", m),
    info: (m) => push("info", m),
  });

  return (
    <ToastContext.Provider value={api.current}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[80] flex w-[340px] max-w-[calc(100vw-2rem)] flex-col gap-2">
        <AnimatePresence initial={false}>
          {toasts.map((t) => {
            const { color, Icon } = STYLES[t.kind];
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, x: 24 }}
                transition={{ type: "tween", duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="glass pointer-events-auto flex items-start gap-2.5 border-l-2 p-3 text-sm shadow-xl"
                style={{ borderLeftColor: color }}
              >
                <Icon size={16} style={{ color }} className="mt-0.5 shrink-0" />
                <span className="min-w-0 flex-1 text-ink">{t.message}</span>
                <button
                  onClick={() => dismiss(t.id)}
                  className="shrink-0 text-slatey transition hover:text-ink"
                  title="Dismiss"
                >
                  <X size={14} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within a ToastProvider");
  return ctx;
}
